/**
 * PIV-48: background activity per bot and the small to-do window in the bot view.
 * The shapes are the Teil A contract (bots.activity, bots.todo in contracts/src/bot-activity.ts).
 */
import type { BotActivity, TodoItem, TodoItemStatus } from "@rakazo/contracts";
import { TodoItemStatusSchema } from "@rakazo/contracts";

/** Ticket statuses the to-do window knows, as the contract delivers them (closed counts as done). */
export const BOT_TODO_STATUSES = TodoItemStatusSchema.options;
/** One of BOT_TODO_STATUSES, i.e. TodoItemStatus from the contract. */
export type BotTodoStatus = TodoItemStatus;

/** Whether a bot works in the background right now, and on which ticket. */
export type { BotActivity };

/** One row of the to-do window as bots.todo delivers it: ticket reference, label (plain text) and status. */
export type BotTodoItem = TodoItem;

/** Statuses where the chat itself already shows the bot's state; background activity must not hide them. */
const CHAT_STATUSES_THAT_WIN = new Set([
  "queued",
  "leased",
  "running",
  "waiting_input",
  "waiting_takeover",
  "failed",
]);

/**
 * Status for the roster avatar and line. A ticket session or routine runs without the chat
 * changing, so an idle chat with an active background run reads as running.
 */
export function rosterStatusWithActivity(
  chatStatus: string,
  activity: Pick<BotActivity, "active"> | null | undefined,
): string {
  if (!activity?.active) return chatStatus;
  return CHAT_STATUSES_THAT_WIN.has(chatStatus) ? chatStatus : "running";
}

/** Indexes the activity rows by bot id; missing data gives an empty map. */
export function activityByBot(rows: readonly BotActivity[] | null | undefined) {
  const map = new Map<string, BotActivity>();
  for (const row of rows ?? []) map.set(row.botId, row);
  return map;
}

/** Top to bottom: what is coming up, then what is stuck or waiting, then work in progress, then the last finished. */
const GROUP_ORDER: Record<BotTodoStatus, number> = {
  todo: 0,
  blocked: 1,
  review: 2,
  doing: 3,
  done: 4,
};

function time(iso: string): number {
  const value = Date.parse(iso);
  return Number.isNaN(value) ? 0 : value;
}

/**
 * Orders the to-do rows: newest first within each group, and only the most recent
 * finished ticket stays (older finished ones disappear as soon as a newer one is done).
 */
export function orderTodoItems<T extends BotTodoItem>(items: readonly T[]): T[] {
  const open = items.filter((item) => item.status !== "done");
  const lastDone = items
    .filter((item) => item.status === "done")
    .reduce<T | null>(
      (latest, item) => (!latest || time(item.updatedAt) > time(latest.updatedAt) ? item : latest),
      null,
    );
  const sorted = [...open].sort(
    (a, b) =>
      GROUP_ORDER[a.status] - GROUP_ORDER[b.status] || time(b.updatedAt) - time(a.updatedAt),
  );
  return lastDone ? [...sorted, lastDone] : sorted;
}

const COLLAPSED_PREFIX = "rakazo:bot-todo-collapsed:";

/** The window starts open; collapsing is remembered per user. */
export function readBotTodoCollapsed(userId: string | null | undefined): boolean {
  if (!userId) return false;
  try {
    return window.localStorage.getItem(`${COLLAPSED_PREFIX}${userId}`) === "1";
  } catch {
    return false;
  }
}

/** Remembers per user whether the to-do window is collapsed; storage errors are ignored. */
export function writeBotTodoCollapsed(userId: string | null | undefined, collapsed: boolean): void {
  if (!userId) return;
  const key = `${COLLAPSED_PREFIX}${userId}`;
  try {
    if (collapsed) window.localStorage.setItem(key, "1");
    else window.localStorage.removeItem(key);
  } catch {
    // Storage can be unavailable (private mode); the in-memory state still applies.
  }
}

/**
 * RAK-91: the extra fields bots.todo delivers per row (RAK-92 contract, shared/rakazo/TODO-LEISTE-API.md).
 * Optional so rows of older servers still render; a missing or null value shows as "–", never as 0.
 */
export type BotTodoDetails = {
  id?: string;
  ownerBotId?: string | null;
  ownerName?: string | null;
  working?: boolean;
  blockedReason?: string | null;
  progress?: { done: number; total: number } | null;
  startedAt?: string | null;
  durationMs?: number | null;
  costUsd?: number | null;
  totalTokens?: number | null;
};

/** One row of the to-do rail with its details. */
export type BotTodoRow = BotTodoItem & BotTodoDetails;

/** Rail width in px: today's width is the minimum. */
export const TODO_RAIL_MIN_PX = 268;
/** From this width on every row shows its details; below, one row opens on click. */
export const TODO_RAIL_WIDE_PX = 440;
/** The reading column keeps at least this much room beside the rail (plus the 2rem gap). */
export const TODO_RAIL_CENTER_MIN_PX = 360;
/** Arrow keys on the grip move the rail by this much. */
export const TODO_RAIL_KEY_STEP_PX = 16;

/** Largest rail width: half the window, and the reading column keeps its minimum. */
export function maxTodoRailWidth(windowWidth: number, containerWidth: number): number {
  return Math.max(
    TODO_RAIL_MIN_PX,
    Math.min(
      Math.floor(windowWidth / 2),
      Math.floor(containerWidth - TODO_RAIL_CENTER_MIN_PX - 32),
    ),
  );
}

/** Keeps a rail width between the minimum and maxTodoRailWidth. */
export function clampTodoRailWidth(
  width: number,
  windowWidth: number,
  containerWidth: number,
): number {
  if (!Number.isFinite(width)) return TODO_RAIL_MIN_PX;
  const max = maxTodoRailWidth(windowWidth, containerWidth);
  return Math.round(Math.min(max, Math.max(TODO_RAIL_MIN_PX, width)));
}

const RAIL_WIDTH_KEY = "rakazo:todo-rail-width";

/**
 * The stored rail width of this device, or the minimum. Clamped to the window right away (G3), so
 * a stored 5000 never becomes state; the reading column's room is checked again by CSS and grip.
 */
export function readTodoRailWidth(windowWidth: number = window.innerWidth): number {
  try {
    const value = Number(window.localStorage.getItem(RAIL_WIDTH_KEY));
    return Number.isFinite(value) && value >= TODO_RAIL_MIN_PX
      ? clampTodoRailWidth(value, windowWidth, windowWidth)
      : TODO_RAIL_MIN_PX;
  } catch {
    return TODO_RAIL_MIN_PX;
  }
}

/** Remembers the rail width per device; the minimum clears the entry. Storage errors are ignored. */
export function writeTodoRailWidth(width: number): void {
  try {
    if (width <= TODO_RAIL_MIN_PX) window.localStorage.removeItem(RAIL_WIDTH_KEY);
    else window.localStorage.setItem(RAIL_WIDTH_KEY, String(Math.round(width)));
  } catch {
    // Storage can be unavailable (private mode); the in-memory width still applies.
  }
}

/** No-break space: a value ("1 h 12 min", "3,40 $", "2 von 5") never wraps inside itself. */
const NBSP = "\u00a0";

/** One unit of a to-do duration: up to two of them, the larger first ("1 h 12 min"). */
export type TodoDurationPart = { value: number; unit: "d" | "h" | "min" };

/**
 * RAK-91 M1: a to-do duration as numbers, the words come from the active language.
 * "under" below one minute, null when unknown.
 */
export function todoDurationParts(
  ms: number | null | undefined,
): TodoDurationPart[] | "under" | null {
  if (ms == null || !Number.isFinite(ms) || ms < 0) return null;
  const minutes = Math.floor(ms / 60_000);
  if (minutes < 1) return "under";
  if (minutes < 60) return [{ value: minutes, unit: "min" }];
  const hours = Math.floor(minutes / 60);
  if (hours < 24) {
    const rest = minutes % 60;
    return rest
      ? [
          { value: hours, unit: "h" },
          { value: rest, unit: "min" },
        ]
      : [{ value: hours, unit: "h" }];
  }
  const days = Math.floor(hours / 24);
  const restHours = hours % 24;
  return restHours
    ? [
        { value: days, unit: "d" },
        { value: restHours, unit: "h" },
      ]
    : [{ value: days, unit: "d" }];
}

/**
 * RAK-91 M1: the words of the detail line, from the active language (Lingui and i18n.locale in
 * the window). The line itself, its gaps and its order stay here.
 */
export type TodoDetailWords = {
  /** "2 of 5". */
  progress: (done: number, total: number) => string;
  /** "under 1 min". */
  under: string;
  /** "12 min", "1 h", "2 d". */
  unit: (part: TodoDurationPart) => string;
  /** "$3.40", "3,40 $". */
  cost: (usd: number) => string;
};

/** Every space inside a value becomes a no-break space. */
function keepTogether(value: string): string {
  return value.replace(/\s/g, NBSP);
}

/**
 * The one detail line, the same in the narrow rail, the wide rail and the team view:
 * "2 of 5 · 1 h 12 min · $3.40" in the active language. A missing value shows as "–"; a row
 * without any value gets no line at all (null), never a line of dashes.
 */
export function formatTodoDetailLine(row: BotTodoDetails, words: TodoDetailWords): string | null {
  const progress = row.progress;
  const hasProgress =
    progress != null &&
    Number.isFinite(progress.done) &&
    Number.isFinite(progress.total) &&
    progress.total > 0;
  const duration = todoDurationParts(row.durationMs);
  const usd = row.costUsd;
  const hasCost = usd != null && Number.isFinite(usd) && usd >= 0;
  const parts = [
    hasProgress
      ? words.progress(Math.max(0, Math.round(progress.done)), Math.round(progress.total))
      : null,
    duration === null
      ? null
      : duration === "under"
        ? words.under
        : duration.map(words.unit).join(" "),
    hasCost ? words.cost(usd) : null,
  ];
  if (parts.every((part) => part === null)) return null;
  return parts.map((part) => (part === null ? "–" : keepTogether(part))).join(" · ");
}

/** One bot's block in the team view. */
export type TodoOwnerGroup = {
  ownerBotId: string | null;
  ownerName: string | null;
  items: BotTodoRow[];
};

/**
 * Team view (Johann: variant A): rows grouped by owning bot. The viewed bot's own rows
 * come first, then the other bots in order of their first row; inside a group the
 * server order (orderTodoItems) is kept.
 */
export function groupTodoRowsByOwner(
  rows: readonly BotTodoRow[],
  selfBotId: string | null | undefined,
): TodoOwnerGroup[] {
  const groups = new Map<string, TodoOwnerGroup>();
  for (const row of rows) {
    const ownerId = row.ownerBotId ?? selfBotId ?? null;
    const key = ownerId ?? "";
    let group = groups.get(key);
    if (!group) {
      group = { ownerBotId: ownerId, ownerName: row.ownerName ?? null, items: [] };
      groups.set(key, group);
    }
    if (!group.ownerName && row.ownerName) group.ownerName = row.ownerName;
    group.items.push(row);
  }
  const list = [...groups.values()];
  const selfIndex = list.findIndex((group) => group.ownerBotId === (selfBotId ?? null));
  if (selfIndex > 0) list.unshift(...list.splice(selfIndex, 1));
  return list;
}
